import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Link, Route, Routes, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useCart } from '@/lib/store';
import { ApiError, api, type OrderDTO, type ProductDTO } from '@/lib/api';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function Layout({ children }: { children: React.ReactNode }) {
  const cartCount = useCart((s) => s.totalQuantity());
  const user = useAuth((s) => s.user);
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="text-lg font-semibold">
            Storefront
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hover:underline">
              Products
            </Link>
            <Link to="/cart" className="hover:underline">
              Cart ({cartCount})
            </Link>
            {user ? (
              <Link to="/admin" className="hover:underline">
                Admin
              </Link>
            ) : (
              <Link to="/login" className="hover:underline">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">{children}</main>
      <footer className="border-t bg-white py-4 text-center text-xs text-neutral-500">
        Built on Cloudflare Pages + D1
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home — product catalog
// ---------------------------------------------------------------------------

function HomePage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const add = useCart((s) => s.add);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['products', search, category, page],
    queryFn: () =>
      api.products.list({
        search: search || undefined,
        category: category || undefined,
        page,
      }),
  });

  return (
    <section>
      <h1 className="mb-4 text-2xl font-semibold">Products</h1>

      <div className="mb-6 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search products…"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="max-w-xs flex-1 rounded border px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Category"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="w-36 rounded border px-3 py-2 text-sm"
        />
      </div>

      {isLoading && <p className="text-neutral-400">Loading…</p>}
      {isError && <p className="text-red-500">Failed to load products.</p>}

      {data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-neutral-500">No products found.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {data.items.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={() => add(p)} />
              ))}
            </div>
          )}

          {data.pagination.pages > 1 && (
            <div className="mt-6 flex items-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((n) => n - 1)}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm">
                {page} / {data.pagination.pages}
              </span>
              <button
                disabled={page >= data.pagination.pages}
                onClick={() => setPage((n) => n + 1)}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ProductCard({ product: p, onAdd }: { product: ProductDTO; onAdd: () => void }) {
  return (
    <div className="flex flex-col rounded border">
      {p.imageUrl ? (
        <img
          src={p.imageUrl}
          alt={p.name}
          className="h-40 w-full rounded-t object-cover"
        />
      ) : (
        <div className="flex h-40 items-center justify-center rounded-t bg-neutral-100 text-sm text-neutral-400">
          No image
        </div>
      )}
      <div className="flex flex-1 flex-col p-3">
        <p className="line-clamp-2 text-sm font-medium">{p.name}</p>
        {p.category && (
          <p className="mt-0.5 text-xs text-neutral-500">{p.category}</p>
        )}
        <p className="mt-auto pt-2 font-semibold">${(p.priceCents / 100).toFixed(2)}</p>
        <p className="text-xs text-neutral-500">
          {p.stock > 0 ? `${p.stock} in stock` : 'Out of stock'}
        </p>
        <button
          disabled={p.stock === 0}
          onClick={onAdd}
          className="mt-2 rounded bg-neutral-900 px-3 py-1.5 text-xs text-white hover:bg-neutral-700 disabled:opacity-40"
        >
          Add to cart
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cart + checkout
// ---------------------------------------------------------------------------

function CartPage() {
  const lines = useCart((s) => s.lines);
  const totalCents = useCart((s) => s.totalCents());
  const setQty = useCart((s) => s.setQuantity);
  const remove = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    shippingAddress: '',
    notes: '',
  });
  const [error, setError] = useState('');
  const [orderNo, setOrderNo] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.orders.create({
        customerName: form.customerName,
        customerEmail: form.customerEmail || null,
        customerPhone: form.customerPhone || null,
        shippingAddress: form.shippingAddress || null,
        notes: form.notes || null,
        items: lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
      }),
    onSuccess: (order) => {
      clear();
      setOrderNo(order.orderNo);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to place order');
    },
  });

  if (orderNo) {
    return (
      <section>
        <h1 className="text-2xl font-semibold">Order placed!</h1>
        <p className="mt-2 text-neutral-600">
          Order number: <strong>{orderNo}</strong>
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 rounded bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700"
        >
          Continue shopping
        </button>
      </section>
    );
  }

  return (
    <section>
      <h1 className="text-2xl font-semibold">Cart</h1>

      {lines.length === 0 ? (
        <p className="mt-2 text-neutral-600">Your cart is empty.</p>
      ) : (
        <>
          <ul className="mt-4 divide-y">
            {lines.map((line) => (
              <li key={line.productId} className="flex items-center gap-4 py-3">
                <span className="flex-1 text-sm">{line.name}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setQty(line.productId, line.quantity - 1)}
                    className="flex h-6 w-6 items-center justify-center rounded border text-sm"
                  >
                    −
                  </button>
                  <span className="w-6 text-center text-sm">{line.quantity}</span>
                  <button
                    onClick={() => setQty(line.productId, line.quantity + 1)}
                    className="flex h-6 w-6 items-center justify-center rounded border text-sm"
                  >
                    +
                  </button>
                </div>
                <span className="w-20 text-right text-sm font-medium">
                  ${((line.priceCents * line.quantity) / 100).toFixed(2)}
                </span>
                <button
                  onClick={() => remove(line.productId)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          <p className="mt-3 text-right font-semibold">
            Total: ${(totalCents / 100).toFixed(2)}
          </p>

          <h2 className="mt-8 text-lg font-semibold">Checkout</h2>
          <form
            className="mt-4 grid max-w-md gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError('');
              mutation.mutate();
            }}
          >
            <input
              required
              placeholder="Full name *"
              value={form.customerName}
              onChange={(e) =>
                setForm((f) => ({ ...f, customerName: e.target.value }))
              }
              className="rounded border px-3 py-2 text-sm"
            />
            <input
              type="email"
              placeholder="Email"
              value={form.customerEmail}
              onChange={(e) =>
                setForm((f) => ({ ...f, customerEmail: e.target.value }))
              }
              className="rounded border px-3 py-2 text-sm"
            />
            <input
              placeholder="Phone"
              value={form.customerPhone}
              onChange={(e) =>
                setForm((f) => ({ ...f, customerPhone: e.target.value }))
              }
              className="rounded border px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Shipping address"
              rows={2}
              value={form.shippingAddress}
              onChange={(e) =>
                setForm((f) => ({ ...f, shippingAddress: e.target.value }))
              }
              className="rounded border px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Notes (optional)"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="rounded border px-3 py-2 text-sm"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Placing order…' : 'Place order'}
            </button>
          </form>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const login = useAuth((s) => s.login);
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/admin');
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto mt-12 max-w-sm">
      <h1 className="mb-6 text-2xl font-semibold">Admin sign in</h1>
      <form onSubmit={handleSubmit} className="grid gap-4">
        <input
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded border px-3 py-2 text-sm"
        />
        <input
          type="password"
          required
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded border px-3 py-2 text-sm"
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Admin — orders tab
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderDTO['status'], string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-neutral-100 text-neutral-600',
};

const NEXT_STATUSES: Record<OrderDTO['status'], OrderDTO['status'][]> = {
  pending: ['paid', 'cancelled'],
  paid: ['shipped', 'cancelled'],
  shipped: ['delivered', 'cancelled'],
  delivered: [],
  cancelled: [],
};

function OrdersTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<OrderDTO['status'] | ''>('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-orders', page, statusFilter],
    queryFn: () => api.orders.list({ page, status: statusFilter || undefined }),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: OrderDTO['status'] }) =>
      api.orders.updateStatus(id, status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-orders'] }),
  });

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium">Filter status:</label>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as OrderDTO['status'] | '');
            setPage(1);
          }}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">All</option>
          {(['pending', 'paid', 'shipped', 'delivered', 'cancelled'] as const).map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-neutral-400">Loading…</p>}

      {data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-neutral-500">No orders found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-neutral-500">
                    <th className="pb-2 pr-4">Order #</th>
                    <th className="pb-2 pr-4">Customer</th>
                    <th className="pb-2 pr-4">Total</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2 pr-4">Date</th>
                    <th className="pb-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((order) => (
                    <tr key={order.id} className="py-2">
                      <td className="py-2 pr-4 font-mono text-xs">{order.orderNo}</td>
                      <td className="py-2 pr-4">{order.customerName}</td>
                      <td className="py-2 pr-4">
                        ${(order.totalAmountCents / 100).toFixed(2)}
                      </td>
                      <td className="py-2 pr-4">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status]}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-neutral-500">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          {NEXT_STATUSES[order.status].map((next) => (
                            <button
                              key={next}
                              disabled={updateStatus.isPending}
                              onClick={() =>
                                updateStatus.mutate({ id: order.id, status: next })
                              }
                              className="rounded border px-2 py-0.5 text-xs hover:bg-neutral-50 disabled:opacity-40"
                            >
                              → {next}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.pagination.pages > 1 && (
            <div className="mt-4 flex items-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((n) => n - 1)}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm">
                {page} / {data.pagination.pages}
              </span>
              <button
                disabled={page >= data.pagination.pages}
                onClick={() => setPage((n) => n + 1)}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin — products tab
// ---------------------------------------------------------------------------

type ProductFormState = {
  name: string;
  description: string;
  price: string;
  stock: string;
  category: string;
  imageUrl: string;
};

const emptyForm: ProductFormState = {
  name: '',
  description: '',
  price: '',
  stock: '0',
  category: '',
  imageUrl: '',
};

function productFormToInput(f: ProductFormState) {
  return {
    name: f.name,
    description: f.description || null,
    priceCents: Math.round(parseFloat(f.price || '0') * 100),
    stock: parseInt(f.stock || '0', 10),
    category: f.category || null,
    imageUrl: f.imageUrl || null,
  };
}

function productToForm(p: ProductDTO): ProductFormState {
  return {
    name: p.name,
    description: p.description ?? '',
    price: (p.priceCents / 100).toFixed(2),
    stock: String(p.stock),
    category: p.category ?? '',
    imageUrl: p.imageUrl ?? '',
  };
}

function ProductsTab() {
  const [page, setPage] = useState(1);
  const [editTarget, setEditTarget] = useState<ProductDTO | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-products', page],
    queryFn: () => api.products.list({ page }),
  });

  const createMut = useMutation({
    mutationFn: () => api.products.create(productFormToInput(form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-products'] });
      setShowCreate(false);
      setForm(emptyForm);
      setFormError('');
    },
    onError: (err) =>
      setFormError(err instanceof ApiError ? err.message : 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: () => api.products.update(editTarget!.id, productFormToInput(form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-products'] });
      setEditTarget(null);
      setForm(emptyForm);
      setFormError('');
    },
    onError: (err) =>
      setFormError(err instanceof ApiError ? err.message : 'Update failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.products.delete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-products'] }),
  });

  const openEdit = (p: ProductDTO) => {
    setEditTarget(p);
    setForm(productToForm(p));
    setShowCreate(false);
    setFormError('');
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setShowCreate(true);
    setFormError('');
  };

  const cancelForm = () => {
    setEditTarget(null);
    setShowCreate(false);
    setForm(emptyForm);
    setFormError('');
  };

  const isEditing = showCreate || editTarget !== null;
  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-neutral-500">
          {data ? `${data.pagination.total} products` : ''}
        </span>
        <button
          onClick={openCreate}
          className="rounded bg-neutral-900 px-3 py-1.5 text-sm text-white hover:bg-neutral-700"
        >
          + New product
        </button>
      </div>

      {isEditing && (
        <form
          className="mb-6 grid max-w-lg gap-3 rounded border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError('');
            if (editTarget) updateMut.mutate();
            else createMut.mutate();
          }}
        >
          <h3 className="font-medium">
            {editTarget ? 'Edit product' : 'New product'}
          </h3>
          <input
            required
            placeholder="Name *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="rounded border px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Description"
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="rounded border px-3 py-2 text-sm"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              required
              type="number"
              step="0.01"
              min="0"
              placeholder="Price (USD) *"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              className="rounded border px-3 py-2 text-sm"
            />
            <input
              required
              type="number"
              min="0"
              placeholder="Stock *"
              value={form.stock}
              onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))}
              className="rounded border px-3 py-2 text-sm"
            />
          </div>
          <input
            placeholder="Category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="rounded border px-3 py-2 text-sm"
          />
          <input
            type="url"
            placeholder="Image URL"
            value={form.imageUrl}
            onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
            className="rounded border px-3 py-2 text-sm"
          />
          {formError && <p className="text-sm text-red-500">{formError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSaving}
              className="rounded bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : editTarget ? 'Save changes' : 'Create'}
            </button>
            <button
              type="button"
              onClick={cancelForm}
              className="rounded border px-4 py-2 text-sm hover:bg-neutral-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {isLoading && <p className="text-neutral-400">Loading…</p>}

      {data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-neutral-500">No products yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-neutral-500">
                    <th className="pb-2 pr-4">Name</th>
                    <th className="pb-2 pr-4">Category</th>
                    <th className="pb-2 pr-4">Price</th>
                    <th className="pb-2 pr-4">Stock</th>
                    <th className="pb-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((p) => (
                    <tr key={p.id}>
                      <td className="py-2 pr-4 font-medium">{p.name}</td>
                      <td className="py-2 pr-4 text-neutral-500">
                        {p.category ?? '—'}
                      </td>
                      <td className="py-2 pr-4">
                        ${(p.priceCents / 100).toFixed(2)}
                      </td>
                      <td className="py-2 pr-4">{p.stock}</td>
                      <td className="py-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEdit(p)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            disabled={deleteMut.isPending}
                            onClick={() => {
                              if (confirm(`Delete "${p.name}"?`))
                                deleteMut.mutate(p.id);
                            }}
                            className="text-xs text-red-500 hover:underline disabled:opacity-40"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.pagination.pages > 1 && (
            <div className="mt-4 flex items-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((n) => n - 1)}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm">
                {page} / {data.pagination.pages}
              </span>
              <button
                disabled={page >= data.pagination.pages}
                onClick={() => setPage((n) => n + 1)}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin page (wrapper with auth guard + tab bar)
// ---------------------------------------------------------------------------

function AdminPage() {
  const user = useAuth((s) => s.user);
  const authLoading = useAuth((s) => s.loading);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const [tab, setTab] = useState<'orders' | 'products'>('orders');

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  if (!user) return null;

  return (
    <section>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-neutral-500">{user.email}</span>
          <button
            onClick={() => logout().then(() => navigate('/'))}
            className="text-neutral-600 hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="mb-6 flex gap-4 border-b">
        {(['orders', 'products'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`pb-2 px-1 text-sm capitalize ${
              tab === t
                ? 'border-b-2 border-neutral-900 font-medium'
                : 'text-neutral-500 hover:text-neutral-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'orders' ? <OrdersTab /> : <ProductsTab />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// App root
// ---------------------------------------------------------------------------

export default function App() {
  const refreshAuth = useAuth((s) => s.refresh);
  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
